<template>
    <div class="page-layout">
        <search-filter-manager
            v-if="gridApi"
            :grid-api="gridApi"
            :grid-url="'/api/processcharges/trip/grid/saved'"
            :refresh-server-side="refreshServerSide"
            @filter-loaded="filterHasLoaded"
            @onSearch="handleFilterOrSearch"
        />
        <div class="page-content">
            <bulk-actions
                :selected-jobs="selectedRows"
                :trigger-bulk-recalculate="triggerBulkRecalculate"
                type="trip"
            />
            <ag-grid-vue
                class="grid-display"
                :class="gridThemeClass"
                row-model-type="serverSide"
                :column-defs="columnDefs"
                :default-col-def="defaultColDef"
                :grid-options="gridOptions"
                :suppress-excel-export="false"
                :row-selection="rowSelection"
                :side-bar="gridSideBar"
                @grid-ready="onGridReady"
                @selection-changed="onSelectionChanged"
            />
        </div>
    </div>
</template>

<script>
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridVue } from 'ag-grid-vue';
import { mapGetters, mapActions } from 'vuex';
import { GeneralMixin, AgGridMixin, ProcessChargesMixin } from '@/mixins';
import { handleRequests, showErrorMessage } from '@/helpers';
import LinkRenderer from '@/AgGridComponents/link-renderer';
import PageHeaderCheckbox from '@/AgGridComponents/page-header-checkbox';
import { HUB_NAMES, HUB_ACTIONS, FEATURE_NAMES, SIGNALR_CONNECTION_STATE } from '@/utils/constants';
import ActionRendererProcessCharges from '@/pages/ProcessChargesAgGrid/components/action-renderer';
import { PROCESS_CHARGES_DATA_DEFAULT } from '@/pages/ProcessChargesAgGrid/processChargesConstants';

import SearchFilterManager from '@/AgGridComponents/SearchFilterManager';

import { FeatureManager } from '@/directives';
import BulkActions from '@/pages/ProcessChargesAgGrid/components/BulkActions';
import { buildProcessChargesTripsColumns } from './processChargesTripsColumns';

// NOTE: Prototype page for reviewing Process Charges Trips using AgGrid
// Endpoint: api/processcharges/trip/grid/search
export default {
    name: 'ProcessChargesTripsGrid',
    components: {
        AgGridVue,
        // eslint-disable-next-line vue/no-unused-components
        LinkRenderer,
        // eslint-disable-next-line vue/no-unused-components
        ActionRendererProcessCharges,
        // eslint-disable-next-line vue/no-unused-components
        PageHeaderCheckbox,
        BulkActions,
        SearchFilterManager
    },
    mixins: [GeneralMixin, AgGridMixin, ProcessChargesMixin, FeatureManager],
    data() {
        return {
            ...PROCESS_CHARGES_DATA_DEFAULT,
            columnDefs: [],
            signalRHandlers: [],
            signalRTimeout: null,
            searchText: '',
            hasGridFilterLoaded: false,
            processChargesTripKeys: [
                'driverHours',
                'hours',
                'hoursCharged',
                'hoursCosted',
                'nettCharge',
                'nettCost',
                'baseCharge',
                'fuelLevyCharge',
                'baseCost',
                'fuelLevyCost',
                'margin',
                'hoursCharged',
                'chargePerHour',
                'marginPercent',
                'costPerHour'
            ],
            isCostApprovalFeatureEnabled: this.$_hasFeature(FEATURE_NAMES.JobsCostApproval),
            isChargeApprovalFeatureEnabled: this.$_hasFeature(FEATURE_NAMES.JobsChargeApproval),
            selectedRows: [],
            grandTotals: null,
            lastCacheBlockSize: null
        };
    },
    created() {
        this.lastCacheBlockSize = this.gridOptions.cacheBlockSize;
        this.gridOptions.getRowId = (params) => params.data.tripId;
        // eslint-disable-next-line no-unused-expressions
        this.gridOptions.onPaginationChanged = (params) => {
            this.updateCacheBlockDefaultSize(params.api);
            this.updatePinnedBottomRows(params.api);
        };
        this.buildColumns();
    },
    computed: {
        ...mapGetters({
            getTeamMembers: 'team/teamMembers',
            getTeamRegions: 'team/teamRegions',
            getConnectionStatus: 'signalR/getConnectionStatus',
            getConnection: 'signalR/getConnection'
        })
    },
    mounted() {
        this.signalRHandlers.push(
            // might move these 3 to the bulk action component in the next PR
            {
                action: 'ProcessRecalculationCompleted',
                handler: async (totalSuccess, failedRecords) => {
                    this.handleRecalculationCompleted(totalSuccess, failedRecords);
                }
            },
            {
                action: 'ProcessRecalculationTotal',
                handler: async (totalsToBeCalculated) => {
                    this.handleTotalToBeRecalculated(totalsToBeCalculated);
                }
            },
            {
                action: 'ProcessRecalculationFailed',
                handler: (error) => {
                    this.handleRecalculationFailed(error);
                }
            },
            // TRIP SPECIFIC
            {
                action: 'ProcessRecalculationTripComplete',
                handler: async (tripId) => {
                    this.handleTripRecalculation(tripId);
                }
            },
            {
                action: 'TripApprovalStatusUpdated',
                handler: async (tripId, approved, unapproved) => {
                    this.onTripApprovalStatusUpdated(tripId, approved, unapproved);
                }
            }
        );

        this.setupSignalR();
        this.FETCH_SERVICE_PACKAGES();
    },
    beforeDestroy() {
        clearTimeout(this.signalRTimeout);
        this.removeSignalRHandlers();
    },
    methods: {
        ...mapActions('team', ['FETCH_TEAM_MEMBERS', 'FETCH_TEAM_REGIONS']),
        ...mapActions('processCharges', ['FETCH_SERVICE_PACKAGES']),
        buildColumns() {
            this.columnDefs = buildProcessChargesTripsColumns(this);
        },
        onGridReady(params) {
            this.gridApi = params.api;

            if (!this.hasGridFilterLoaded) 
                return;

            this.setupDataList();
        },
        setupDataList() {
            const datasource = this.$_createDataSource(
                '/api/processcharges/trip',
                this.gridApi,
                () => this.searchText, // getter function to get current search text
                null, // no data transformer
                (grandTotals) => this.handleGrandTotals(grandTotals) // grand totals callback
            );

            this.gridApi.setServerSideDatasource(datasource);
        },
        handleGrandTotals(grandTotals) {
            this.grandTotals = grandTotals;
            this.updatePinnedBottomRows(this.gridApi);
        },
        updatePinnedBottomRows(api) {
            this.$_updatePinnedBottomRowsWithGrandTotals(
                api,
                this.grandTotals,
                this.processChargesTripKeys,
                'tripReference'
            );
        },
        updateCacheBlockDefaultSize(api) {
            const currentPageSize = api.paginationGetPageSize();
            if (this.lastCacheBlockSize !== currentPageSize) {
                this.lastCacheBlockSize = currentPageSize;
                api.setCacheBlockSize(currentPageSize);
            }
        },
        onSelectionChanged() {
            const selectedRows = this.gridApi.getSelectedRows();
            this.selectedRows = selectedRows;
        },
        async loadCustomerList(params) {
            try {
                const endpoint = '/api/customers/allCustomers';
                const response = await handleRequests(endpoint);

                let customerList = [];

                if (response.data.customers && response.data.customers.length > 0) {
                    customerList = response.data.customers.map((cl) => ({
                        customerId: cl.customerId,
                        name: cl.name
                    }));
                    customerList.push({ name: '(Blank)', customerId: null });
                } else {
                    customerList = [];
                }
                params.success(customerList);
            } catch (error) {
                console.error('Error loading customer list:', error);
                const message = 'Error in getting the customer list';
                showErrorMessage(this, message, error);
                params.success([]);
            }
        },
        async loadTeamMembers(params) {
            let teamMemberList = [];

            if (this.getTeamMembers.length <= 0) {
                teamMemberList = await this.FETCH_TEAM_MEMBERS();
            } else {
                teamMemberList = this.getTeamMembers;
            }

            teamMemberList.push({ fullName: '(Blank)', publicUserId: null });
            params.success(teamMemberList);
        },
        async loadTeamRegions(params) {
            let teamRegionList = [];

            if (this.getTeamRegions.length <= 0) {
                teamRegionList = await this.FETCH_TEAM_REGIONS();
            } else {
                teamRegionList = this.getTeamRegions;
            }

            teamRegionList.push({ name: '(Blank)', teamRegionId: null });
            params.success(teamRegionList);
        },
        async loadTeamRegionCode(params) {
            let teamRegionList = [];

            if (this.getTeamRegions.length <= 0) {
                teamRegionList = await this.FETCH_TEAM_REGIONS();
            } else {
                teamRegionList = this.getTeamRegions;
            }

            teamRegionList.push({ teamRegionCode: '(Blank)', teamRegionId: null });

            const transformedList = teamRegionList
                .filter((x) => x.teamRegionCode)
                .map((tr) => ({
                    teamRegionId: tr.teamRegionId,
                    teamRegionCode: tr.teamRegionCode
                }));
            params.success(transformedList);
        },
        async loadRunsList(params) {
            const endpoint = `/api/run-schedules/list`;
            const response = await handleRequests(endpoint, {
                params: {
                    pageNumber: 1,
                    itemsPerPage: 1000
                }
            });

            const transformedList =
                response?.data?.runs?.map((run) => ({
                    runId: run.runScheduleId,
                    runName: run.name
                })) || [];

            params.success(transformedList);
        },
        async loadServicePackageList(params) {
            try {
                const endpoint = `/api/rate-groups/rateRules`;
                const response = await handleRequests(endpoint);

                let servicePackageList = [];

                if (response.data && response.data.length > 0) {
                    servicePackageList = response.data.map((rg) => {
                        return {
                            rateGroupId: rg.rateGroupId,
                            rateGroupName: rg.rateGroupName
                        };
                    });
                    servicePackageList.push({ rateGroupId: null, rateGroupName: '(Blank)' });
                } else {
                    servicePackageList = [];
                }

                params.success(servicePackageList);
            } catch (error) {
                const message = 'Error in getting the rate groups list';
                showErrorMessage(this, message, error);
                params.success([]);
            }
        },
        async setupSignalR() {
            try {
                await this.$store.dispatch('signalR/connect', HUB_NAMES.RatesEngineUpdateHub);
                const connection = this.getConnection(HUB_NAMES.RatesEngineUpdateHub);

                // Verify connection is established before proceeding
                if (!connection || connection.state !== SIGNALR_CONNECTION_STATE.Connected) {
                    throw new Error('SignalR connection not established');
                }

                await connection.invoke(HUB_ACTIONS.JoinToTeamChannel);
                await this.$store.dispatch('signalR/registerHandlers', {
                    hubName: HUB_NAMES.RatesEngineUpdateHub,
                    handlers: this.signalRHandlers
                });
            } catch (e) {
                console.error('Error connecting to rates engine update hub:', e);
            }
        },
        removeSignalRHandlers() {
            try {
                const connection = this.getConnection(HUB_NAMES.RatesEngineUpdateHub);

                if (connection) {
                    this.$store.dispatch('signalR/unregisterHandlers', {
                        hubName: HUB_NAMES.RatesEngineUpdateHub,
                        handlers: this.signalRHandlers
                    });
                    connection.invoke(HUB_ACTIONS.RemoveFromTeamChannel);
                }
            } catch (error) {
                console.error('Error removing SignalR handlers:', error);
            }
        },
        handleTripRecalculation(tripId) {
            const rowNode = this.gridApi.getRowNode(tripId.toString());

            if (rowNode) {
                this.fetchTrip(tripId);
            }
        },
        async fetchTrip(tripId) {
            const api = `/api/processcharges/trip/grid/search`;
            const payload = {
                method: 'post',
                data: {
                    startRow: 0,
                    endRow: 1,
                    filterModel: { tripId: { filterType: 'number', type: 'equals', filter: tripId } },
                    sortModel: [],
                    rowGroupCols: [],
                    valueCols: [],
                    groupKeys: [],
                    pivotCols: []
                }
            };

            const response = await handleRequests(api, payload);
            const rows = response?.data?.responses || [];

            if (rows.length === 0) {
                return;
            }

            const newData = rows[0];

            // Get the old row data to calculate the delta for grand totals
            const rowNode = this.gridApi.getRowNode(tripId.toString());
            const oldData = rowNode?.data;

            const rowDataToUpdate = {
                tripId: tripId.toString(),
                ...newData,
                enableRecalculate: false
            };

            this.gridApi.applyServerSideTransaction({ update: [rowDataToUpdate] });

            // Update grand totals by applying the delta (new values - old values)
            this.updateGrandTotalsWithDelta(oldData, newData);
        },
        updateGrandTotalsWithDelta(oldData, newData) {
            if (!this.grandTotals || !oldData || !newData) {
                return;
            }

            const updatedTotals = { ...this.grandTotals };

            this.processChargesTripKeys.forEach((key) => {
                const oldValue = typeof oldData[key] === 'number' ? oldData[key] : 0;
                const newValue = typeof newData[key] === 'number' ? newData[key] : 0;
                const delta = newValue - oldValue;

                if (typeof updatedTotals[key] === 'number') {
                    updatedTotals[key] += delta;
                }
            });

            this.grandTotals = updatedTotals;
            this.updatePinnedBottomRows(this.gridApi);
        },
        shouldRemoveApprovedRow(approved) {
            if (!approved || !this.gridApi || !this.gridApi.getFilterModel) {
                return false;
            }

            const filterModel = this.gridApi.getFilterModel();

            if (filterModel == null) {
                return false;
            }

            const costFilter = filterModel.isCostsApproved || null;
            const chargeFilter = filterModel.isChargesApproved || null;

            const isNoOnly = (f) =>
                f &&
                f.filterType === 'set' &&
                Array.isArray(f.values) &&
                f.values.length === 1 &&
                f.values[0] === 'false';

            if (this.isCostApprovalFeatureEnabled && this.isChargeApprovalFeatureEnabled) {
                return isNoOnly(costFilter) || isNoOnly(chargeFilter);
            }

            if (this.isCostApprovalFeatureEnabled) {
                return isNoOnly(costFilter);
            }

            if (this.isChargeApprovalFeatureEnabled) {
                return isNoOnly(chargeFilter);
            }

            return false;
        },
        shouldRemoveUnApprovedRow(unapproved) {
            if (!unapproved || !this.gridApi || !this.gridApi.getFilterModel) {
                return false;
            }

            const filterModel = this.gridApi.getFilterModel();

            if (filterModel == null) {
                return false;
            }

            const costFilter = filterModel.isCostsApproved || null;
            const chargeFilter = filterModel.isChargesApproved || null;

            const isYesOnly = (f) =>
                f &&
                f.filterType === 'set' &&
                Array.isArray(f.values) &&
                f.values.length === 1 &&
                f.values[0] === 'true';

            if (this.isCostApprovalFeatureEnabled && this.isChargeApprovalFeatureEnabled) {
                return isYesOnly(costFilter) || isYesOnly(chargeFilter);
            }

            if (this.isCostApprovalFeatureEnabled) {
                return isYesOnly(costFilter);
            }

            if (this.isChargeApprovalFeatureEnabled) {
                return isYesOnly(chargeFilter);
            }

            return false;
        },
        async onTripApprovalStatusUpdated(tripId, approved, unapproved) {
            try {
                const rowNode = this.gridApi.getRowNode(tripId.toString());
                if (this.shouldRemoveApprovedRow(approved)) {
                    if (rowNode) {
                        this.gridApi.applyServerSideTransaction({ remove: [{ tripId: tripId.toString() }] });
                    }
                    return;
                }

                if (this.shouldRemoveUnApprovedRow(unapproved)) {
                    const rowNode = this.gridApi.getRowNode(tripId.toString());
                    if (rowNode) {
                        this.gridApi.applyServerSideTransaction({ remove: [{ tripId: tripId.toString() }] });
                    }
                    return;
                }

                if (rowNode == null) {
                    return;
                }

                let columnValue = null;

                if (approved) {
                    columnValue = true;
                } else if (unapproved) {
                    columnValue = false;
                }

                if (this.isCostApprovalFeatureEnabled && this.isChargeApprovalFeatureEnabled) {
                    rowNode.setDataValue('isCostsApproved', columnValue);
                    rowNode.setDataValue('isChargesApproved', columnValue);
                    return;
                }

                if (this.isCostApprovalFeatureEnabled) {
                    rowNode.setDataValue('isCostsApproved', columnValue);
                    rowNode.setDataValue('isChargesApproved', false);
                    return;
                }

                if (this.isChargeApprovalFeatureEnabled) {
                    rowNode.setDataValue('isCostsApproved', false);
                    rowNode.setDataValue('isChargesApproved', columnValue);
                    return;
                }
            } catch (e) {
                console.error('Error handling TripApprovalStatusUpdated:', e);
            }
        },
        handleRecalculationCompleted(totalSuccess, failedRecords) {
            // to support in the next PR for bulk actions
        },
        handleRecalculationFailed() {
            // to support in the next PR for bulk actions
        },
        handleTotalToBeRecalculated(total) {
            // to support in the next PR for bulk actions
        },
        filterHasLoaded() {
            this.hasGridFilterLoaded = true;
            this.setupDataList();
        },
        // eslint-disable-next-line func-names
        handleFilterOrSearch(val) {
            this.searchText = val;
            this.gridApi.refreshServerSide({ purge: true });
        },
        refreshServerSide() {
            this.gridApi.showLoadingOverlay();
            this.gridApi.refreshServerSide({ purge: true });
        },
        triggerRecalculate(tripId) {
            const rowNode = this.gridApi.getRowNode(tripId.toString());

            const rowDataToUpdate = {
                tripId: tripId.toString(),
                ...rowNode.data,
                externalRecalculate: true
            };

            this.gridApi.applyServerSideTransaction({ update: [rowDataToUpdate] });
        },
        triggerBulkRecalculate(tripIds) {
            const rowDataToUpdates = [];

            tripIds.forEach((tripId) => {
                const rowNode = this.gridApi.getRowNode(tripId.toString());

                const rowDataToUpdate = {
                    tripId: tripId.toString(),
                    ...rowNode.data,
                    externalRecalculate: true
                };

                rowDataToUpdates.push(rowDataToUpdate);
            });

            this.gridApi.applyServerSideTransaction({ update: rowDataToUpdates });
        }
    }
};
</script>

<style scoped lang="scss">
.page-layout {
    display: flex;
    flex-direction: column;
    height: 100%;
}
.page-toolbar {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    gap: 12px;
}
.page-content {
    flex: 1 1 auto;
    min-height: 0;
}
.grid-display {
    width: 100%;
    height: calc(100vh - 160px);
}
.search-textbox {
    padding: 4px 8px;
    width: 280px;
}
</style>
